import { makeStyles } from '@material-ui/core/styles';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { color } from '../../../../app/theme';
import {
  PlatformType,
  ProviderActionType,
  ProviderType,
} from '../../../../app/types';
import IcHelpCircle from '../../../../assets/images/svg/ic-help-circle.svg';
import { setNodeById, setSelectedNode } from '../../../../features/mainSlice';
import Dropdown from '../../../shared/Dropdown';
import Tooltip from '../../../shared/Tooltip';
import MemberPoint from './MemberPoint';
import MembershipTier from './MembershipTier';
import SingleOrderAmount from './SingleOrderAmount';
import TotalOrderAmount from './TotalOrderAmount';
import TotalOrderCount from './TotalOrderCount';
import UserCredit from './UserCredit';

const useStyles = makeStyles(
  {
    root: {
      marginBottom: 36,
    },
    title: {
      fontSize: '15px',
      fontWeight: '500',
      fontStretch: 'normal',
      fontStyle: 'normal',
      lineHeight: '1',
      letterSpacing: 'normal',
      color: color.$3,
      marginBottom: 12,
      display: 'flex',
      alignItems: 'center',
      '& > img': {
        marginLeft: 4,
      },
    },
  },
  {
    name: 'ProviderAction',
  },
);

const ProviderAction = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { provider } = useSelector(state => state.superMarket.EC);
  const {
    selectedNode,
    isErrorNodeIds,
    data: maData,
  } = useSelector(state => state.ma);
  const { data } = selectedNode || {};
  const { action, conditions } = data || {};
  const { platform } = maData || {};
  const isError = !!~isErrorNodeIds.indexOf(selectedNode?.id);

  const onChange = value => {
    const newNode = {
      ...selectedNode,
      data: {
        ...data,
        action: value,
      },
    };

    switch (value) {
      case ProviderActionType.MEMBERSHIPTIER:
        newNode.data.conditions = { type: 'inc' };
        break;
      case ProviderActionType.TOTALORDERCOUNT:
      case ProviderActionType.TOTALORDERAMOUNT:
      case ProviderActionType.SINGLEORDERAMOUNT:
        newNode.data.conditions = 0;
        break;
      case ProviderActionType.USERCREDIT:
      case ProviderActionType.MEMBERPOINT:
        newNode.data.conditions = 'send';
        break;
      case ProviderActionType.BINDEC:
        newNode.data.conditions = 'success';
        break;
      default:
        break;
    }

    dispatch(setSelectedNode(newNode));
    dispatch(setNodeById(newNode));
  };

  const onChangeConditions = value => {
    const newNode = {
      ...selectedNode,
      data: {
        ...data,
        conditions: value,
      },
    };

    dispatch(setSelectedNode(newNode));
    dispatch(setNodeById(newNode));
  };

  const menuItems = useMemo(() => {
    return (
      Object.values(ProviderActionType)
        // livechat 不支援 會員綁定完當下
        .filter(item =>
          platform === PlatformType.LIVECHAT
            ? item !== ProviderActionType.BINDEC
            : true,
        )
        .filter(item => {
          if (provider === ProviderType.SHOPIFY) {
            return item !== ProviderActionType.USERCREDIT;
          }
          if (provider === ProviderType.CYBERBIZ) {
            return item !== ProviderActionType.MEMBERPOINT;
          }
          // Salesforce 目前僅支援：累積訂單數、累積消費金額
          if (provider === ProviderType.SALESFORCE) {
            return [
              ProviderActionType.TOTALORDERCOUNT,
              ProviderActionType.TOTALORDERAMOUNT,
            ].includes(item);
          }
          return true;
        })
        .map(item => ({
          value: item,
          text: t(`ec.${item}`),
        }))
    );
  }, [ProviderActionType, platform]);

  const providerActionContent = useMemo(() => {
    switch (action) {
      case ProviderActionType.MEMBERSHIPTIER:
        return (
          <div className={classes.root}>
            <div className={classes.title}>
              {t(`ec.setting`)}
              {provider !== ProviderType.SHOPLINE && (
                <Tooltip
                  light
                  placement="top"
                  title={t(`membershipTierTooltip-${provider}`)}
                >
                  <img
                    className={classes.marginLeft8}
                    src={IcHelpCircle}
                    alt="alert"
                  />
                </Tooltip>
              )}
            </div>
            <MembershipTier
              conditions={conditions}
              onChange={onChangeConditions}
              isError={isError}
            />
          </div>
        );
      case ProviderActionType.TOTALORDERCOUNT:
        return (
          <div className={classes.root}>
            <div className={classes.title}>{t(`ec.setting`)}</div>
            <TotalOrderCount
              conditions={conditions}
              onChange={onChangeConditions}
              isError={isError}
            />
          </div>
        );
      case ProviderActionType.TOTALORDERAMOUNT:
        return (
          <div className={classes.root}>
            <div className={classes.title}>{t(`ec.setting`)}</div>
            <TotalOrderAmount
              conditions={conditions}
              onChange={onChangeConditions}
            />
          </div>
        );
      case ProviderActionType.SINGLEORDERAMOUNT:
        return (
          <div className={classes.root}>
            <div className={classes.title}>{t(`ec.setting`)}</div>
            <SingleOrderAmount
              conditions={conditions}
              onChange={onChangeConditions}
            />
          </div>
        );
      case ProviderActionType.USERCREDIT:
        return (
          <div className={classes.root}>
            <div className={classes.title}>{t(`ec.setting`)}</div>
            <UserCredit conditions={conditions} onChange={onChangeConditions} />
          </div>
        );
      case ProviderActionType.MEMBERPOINT:
        return (
          <div className={classes.root}>
            <div className={classes.title}>{t(`ec.setting`)}</div>
            <MemberPoint
              conditions={conditions}
              onChange={onChangeConditions}
            />
          </div>
        );
      case ProviderActionType.BINDEC:
      default:
        return null;
    }
  }, [data]);

  return (
    <>
      <div className={classes.root}>
        <div className={classes.title}>
          {t(`${provider}`)}
          <Tooltip light placement="top" title={t('providerTooltip')}>
            <img
              className={classes.marginLeft8}
              src={IcHelpCircle}
              alt="alert"
            />
          </Tooltip>
        </div>
        <Dropdown
          className={classes.dropdown}
          menuItems={menuItems}
          value={action}
          onChange={onChange}
        />
      </div>
      {providerActionContent}
    </>
  );
};

export default ProviderAction;
